import React from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaLeaf, FaHandshake, FaShieldAlt } from 'react-icons/fa';
import Card from '../components/ui/Card';

function AboutPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const TeamMember = ({ name, role, image }) => (
    <motion.div variants={itemVariants} className="group">
      <div className="relative overflow-hidden rounded-2xl mb-4">
        <div className="absolute inset-0 bg-brand-primary/10 group-hover:bg-brand-primary/0 transition-colors z-10 transition duration-300"></div>
        <img src={image} alt={name} className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-500" />
      </div>
      <h3 className="text-xl font-bold text-slate-800">{name}</h3>
      <p className="text-brand-primary font-medium">{role}</p>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16 overflow-hidden">
      {/* Hero Section */}
      <div className="container mx-auto px-4 max-w-7xl mb-20 relative">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-brand-secondary/5 rounded-full blur-3xl"></div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto relative z-10"
        >
          <span className="text-brand-primary font-bold tracking-wider uppercase text-sm mb-4 block">Our Story</span>
          <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6 font-display leading-tight">
            Connecting Communities, <br />Empowering <span className="text-brand-primary">Farmers</span>
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            Krishok Lagbe is more than just a platform; it's a movement to bridge the gap between skilled agricultural workers and the farmers who need them.
          </p>
        </motion.div>
      </div>

      {/* Mission Section */}
      <div className="container mx-auto px-4 max-w-7xl mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-800 mb-6">Our Mission</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              At Krishok Lagbe, we believe that agriculture is the backbone of our economy. But finding reliable labor has always been a challenge.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              We created this platform to provide a seamless, transparent, and efficient way for farmers to find skilled workers, and for workers to find fair employment opportunities.
            </p>

            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-slate-700 font-bold">
                <span className="bg-brand-light p-2 rounded-full text-brand-primary"><FaLeaf /></span>
                Sustainable
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-bold">
                <span className="bg-brand-light p-2 rounded-full text-brand-primary"><FaHandshake /></span>
                Fair
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-bold">
                <span className="bg-brand-light p-2 rounded-full text-brand-primary"><FaShieldAlt /></span>
                Secure
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute inset-0 bg-brand-primary rounded-2xl transform rotate-3 opacity-10"></div>
            <img
              src="https://images.unsplash.com/photo-1595279644598-be920dc2e961?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
              alt="Farmers working"
              className="rounded-2xl shadow-2xl relative z-10 transform -rotate-2 hover:rotate-0 transition-transform duration-500"
            />
          </motion.div>
        </div>
      </div>

      {/* Stats / Values Grid using CSS Grid */}
      <div className="bg-slate-900 py-20 text-white mb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6"
            >
              <div className="text-4xl font-bold text-brand-primary mb-2">5000+</div>
              <div className="text-xl font-semibold mb-2">Verified Workers</div>
              <p className="text-slate-400">Skilled professionals ready to help.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6"
            >
              <div className="text-4xl font-bold text-brand-primary mb-2">1200+</div>
              <div className="text-xl font-semibold mb-2">Successful Jobs</div>
              <p className="text-slate-400">Connecting farmers with workers daily.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6"
            >
              <div className="text-4xl font-bold text-brand-primary mb-2">64</div>
              <div className="text-xl font-semibold mb-2">Districts Covered</div>
              <p className="text-slate-400">Serving communities across the nation.</p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-800">Meet The Team</h2>
          <p className="text-slate-600 mt-2">The passionate individuals behind the platform.</p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10"
        >
          <TeamMember
            name="Surajit Biswas"
            role="Founder & CEO"
            image="https://randomuser.me/api/portraits/men/32.jpg"
          />
          <TeamMember
            name="Jane Smith"
            role="Head of Operations"
            image="https://randomuser.me/api/portraits/women/44.jpg"
          />
          <TeamMember
            name="Peter Jones"
            role="Lead Developer"
            image="https://randomuser.me/api/portraits/men/36.jpg"
          />
          {/* Add more members if needed or keep it clean */}
        </motion.div>
      </div>
    </div>
  );
}

export default AboutPage;